import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  Flex,
  Heading,
  Text,
  Table,
  Thead,
  Tr,
  Tbody,
  Td,
  Spacer,
  Button,
  Th,
  Box,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement, Date, Util } from '@finley-technologies/finley-types';

import {
  PostgresQueryResultWithWarningsAndInputsGQLType,
  TAccountsSumInputValuesGQLType,
  PrincipalBalanceDailyAverageInputValuesGQLType,
  OrgProvidedInputInputValuesGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { PreviewPostgresQueryDrawerHeader } from './lenderView/LenderPostgresQueryComponents';
import { Icon } from 'packages/common-frontend/components/icons';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import { queryInputTypeToIcon } from 'packages/settings/ui/AddQueryInputModal';

const formattedQueryResultValue = ({
  queryType,
  value,
}: {
  queryType: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.QueryType;
  value: Util.Primitive;
}) => {
  switch (queryType) {
    case 'principalBalanceDailyAverage':
    case 'tAccountsSum':
    case 'principalBalancePointInTime':
      if (_.isNumber(value)) {
        return commonUtils.numberToCurrencyWithSymbol({ number: value });
      }
      break;
    default:
      return value;
  }
  return value;
};

const PrincipalBalanceDailyAverageInputValues = ({
  principalBalanceDailyAverageInputs,
}: {
  principalBalanceDailyAverageInputs: PrincipalBalanceDailyAverageInputValuesGQLType;
}) => {
  return (
    <>
      <PreviewPostgresQueryDrawerHeader
        header="Query Input Values"
        marginStyles={{ marginTop: '32px' }}
      />
      <Table mt="24px" borderWidth="1px">
        <Thead>
          <Tr>
            <Th>Date</Th>
            <Th>Principal Balance</Th>
          </Tr>
        </Thead>
        <Tbody>
          {_.map(
            principalBalanceDailyAverageInputs.inputValues,
            (inputValue) => (
              <Tr key={`query-column-header-${inputValue.date}`}>
                <Td>{inputValue.date}</Td>
                <Td>
                  {commonUtils.numberToCurrencyWithSymbol({
                    number: inputValue.value,
                  })}
                </Td>
              </Tr>
            ),
          )}
        </Tbody>
      </Table>
    </>
  );
};

const TAccountsSumResultInputValues = ({
  tAccountsSumInputs,
}: {
  tAccountsSumInputs: TAccountsSumInputValuesGQLType;
}) => {
  const sortedAccounts = _.orderBy(
    tAccountsSumInputs.inputValues,
    ({ counterpartySlug }) => counterpartySlug,
  );
  return (
    <>
      <PreviewPostgresQueryDrawerHeader
        header="Query Input Values"
        marginStyles={{ marginTop: '32px' }}
      />
      <Table mt="24px" borderWidth="1px">
        <Thead>
          <Tr>
            <Th>Counter Party</Th>
            <Th>Account</Th>
            <Th>Value</Th>
          </Tr>
        </Thead>
        <Tbody>
          {_.map(sortedAccounts, (inputValue) => {
            return (
              <Tr
                key={`query-column-header-${inputValue.counterpartySlug}-${inputValue.tAccount}}`}
              >
                <Td>{commonUtils.toTitleCase(inputValue.counterpartySlug)}</Td>
                <Td>{commonUtils.toTitleCase(inputValue.tAccount)}</Td>
                <Td>
                  {commonUtils.numberToCurrencyWithSymbol({
                    number: inputValue.value,
                  })}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </>
  );
};

const OrgProvidedInputResultInputValues = ({
  orgProvidedInputInputs,
  orgProvidedInputKey,
}: {
  orgProvidedInputInputs: OrgProvidedInputInputValuesGQLType;
  orgProvidedInputKey: string;
}) => {
  const sortedAccounts = _.orderBy(
    orgProvidedInputInputs.inputValues,
    ['reportingDate', 'version'],
    ['desc', 'desc'],
  );
  return (
    <>
      <PreviewPostgresQueryDrawerHeader
        header={orgProvidedInputKey}
        marginStyles={{ marginTop: '32px' }}
      />
      <Table mt="24px" borderWidth="1px">
        <Thead>
          <Tr>
            <Th>Reporting Date</Th>
            <Th>Version</Th>
            <Th>Value</Th>
          </Tr>
        </Thead>
        <Tbody>
          {_.map(sortedAccounts, (inputValue) => {
            return (
              <Tr
                key={`query-column-header-${inputValue.reportingDate}-${inputValue.version}}`}
              >
                <Td>{inputValue.reportingDate}</Td>
                <Td>{inputValue.version}</Td>
                <Td>{inputValue.orgProvidedInputValue}</Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </>
  );
};

const StartAndEndDateFields = ({
  startDate,
  endDate,
}: {
  startDate: Util.FullDateStr;
  endDate: Util.FullDateStr;
}) => {
  return (
    <>
      <PreviewPostgresQueryDrawerHeader
        header="Start Date"
        marginStyles={{ marginTop: '32px', marginBottom: '8px' }}
      />
      <Text mt="24px">{startDate}</Text>
      <PreviewPostgresQueryDrawerHeader
        header="End Date"
        marginStyles={{ marginTop: '32px', marginBottom: '8px' }}
      />
      <Text mt="24px">{endDate}</Text>
    </>
  );
};

const OrgProvidedInputFields = ({
  reportingDate,
  reportingPeriod,
}: {
  reportingDate: Util.FullDateStr;
  reportingPeriod: Date.Reporting.ReportingPeriod;
}) => {
  return (
    <>
      <PreviewPostgresQueryDrawerHeader
        header="Reporting Date"
        marginStyles={{ marginTop: '32px', marginBottom: '8px' }}
      />
      <Text mt="24px">{reportingDate}</Text>
      <PreviewPostgresQueryDrawerHeader
        header="Reporting Period"
        marginStyles={{ marginTop: '32px', marginBottom: '8px' }}
      />
      <Text mt="24px">{commonUtils.toTitleCase(reportingPeriod.period)}</Text>
    </>
  );
};

const PrincipalBalancePointInTimeFields = ({
  reportingDate,
}: {
  reportingDate: Util.FullDateStr;
}) => {
  return (
    <>
      <PreviewPostgresQueryDrawerHeader
        header="Reporting Date"
        marginStyles={{ marginTop: '32px', marginBottom: '8px' }}
      />
      <Text mt="24px">{reportingDate}</Text>
    </>
  );
};

type LenderPostgresPreviewBaseProps = {
  isOpen: boolean;
  onClose: () => void;
  queryData?: PostgresQueryResultWithWarningsAndInputsGQLType;
  postgresQuery: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.Query;
  queryType: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.QueryType;
  inputDate: Util.FullDateStr;
  inputDateType: 'reportingDate' | 'triggerDate';
};

interface LenderPrincipalBalanceDailyAveragePreviewDrawerProps
  extends LenderPostgresPreviewBaseProps {
  postgresQuery: CreditAgreement.Config.CommonPostgresQuery.Query.PrincipalBalanceDailyAverageQuery;
  queryType: 'principalBalanceDailyAverage';
  startDate?: Util.FullDateStr;
  endDate?: Util.FullDateStr;
}

interface LenderTAccountsSumPreviewDrawerProps
  extends LenderPostgresPreviewBaseProps {
  postgresQuery: CreditAgreement.Config.CommonPostgresQuery.Query.TAccountsSumQuery;
  queryType: 'tAccountsSum';
  startDate?: Util.FullDateStr;
  endDate?: Util.FullDateStr;
}

interface LenderOrgProvidedInputPreviewDrawerProps
  extends LenderPostgresPreviewBaseProps {
  postgresQuery: CreditAgreement.Config.CommonPostgresQuery.Query.OrgProvidedInputQuery;
  queryType: 'orgProvidedInput';
  reportingDate?: Util.FullDateStr;
  reportingPeriod: Date.Reporting.ReportingPeriod;
  orgProvidedInputKey: string;
}

interface LenderPrincipalBalancePointInTimePreviewDrawerProps
  extends LenderPostgresPreviewBaseProps {
  postgresQuery: CreditAgreement.Config.CommonPostgresQuery.Query.PrincipalBalancePointInTimeQuery;
  queryType: 'principalBalancePointInTime';
  reportingDate?: Util.FullDateStr;
}

type LenderPostgresPreviewDrawerProps =
  | LenderPrincipalBalanceDailyAveragePreviewDrawerProps
  | LenderTAccountsSumPreviewDrawerProps
  | LenderOrgProvidedInputPreviewDrawerProps
  | LenderPrincipalBalancePointInTimePreviewDrawerProps;

export const LenderPostgresPreviewDrawer = (
  props: LenderPostgresPreviewDrawerProps,
) => {
  const {
    isOpen,
    onClose,
    queryData,
    postgresQuery,
    queryType,
    inputDate,
    inputDateType,
  } = props;

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent padding="16px">
        <DrawerHeader>
          <Flex alignItems="center">
            <Icon icon={queryInputTypeToIcon[queryType].icon} />
            <Heading
              as="h2"
              fontSize="24px"
              lineHeight="32px"
              fontWeight={600}
              ml="16px"
            >
              {postgresQuery.name}
            </Heading>
            <Spacer />
            <Button variant="ghost" onClick={onClose}>
              <Icon icon="x" />
            </Button>
          </Flex>
        </DrawerHeader>

        <DrawerBody>
          <PreviewPostgresQueryDrawerHeader
            header={`Preview ${commonUtils.toTitleCase(inputDateType)}`}
            marginStyles={{ marginTop: '8px', marginBottom: '8px' }}
          />
          <Text mt="24px">{inputDate}</Text>

          {queryData ? (
            <>
              {(queryType === 'principalBalanceDailyAverage' ||
                queryType === 'tAccountsSum') &&
                !!props.startDate &&
                !!props.endDate && (
                  <StartAndEndDateFields
                    startDate={props.startDate}
                    endDate={props.endDate}
                  />
                )}

              {queryType === 'orgProvidedInput' &&
                !!props.reportingPeriod &&
                !!props.reportingDate && (
                  <OrgProvidedInputFields
                    reportingDate={props.reportingDate}
                    reportingPeriod={props.reportingPeriod}
                  />
                )}

              {queryType === 'principalBalancePointInTime' &&
                !!props.reportingDate && (
                  <PrincipalBalancePointInTimeFields
                    reportingDate={props.reportingDate}
                  />
                )}

              <PreviewPostgresQueryDrawerHeader
                header="Query Result"
                marginStyles={{ marginTop: '32px' }}
              />
              <Text mt="24px">
                {formattedQueryResultValue({
                  queryType,
                  value: queryData.result.value,
                })}
              </Text>

              {queryData.inputs?.__typename ===
                'PrincipalBalanceDailyAverageInputValues' && (
                <PrincipalBalanceDailyAverageInputValues
                  principalBalanceDailyAverageInputs={queryData.inputs}
                />
              )}
              {queryData.inputs?.__typename === 'TAccountsSumInputValues' && (
                <TAccountsSumResultInputValues
                  tAccountsSumInputs={queryData.inputs}
                />
              )}
              {queryData.inputs?.__typename === 'OrgProvidedInputInputValues' &&
                queryType === 'orgProvidedInput' && (
                  <OrgProvidedInputResultInputValues
                    orgProvidedInputInputs={queryData.inputs}
                    // eslint-disable-next-line react/destructuring-assignment
                    orgProvidedInputKey={props.orgProvidedInputKey}
                  />
                )}
            </>
          ) : (
            <>
              <PreviewPostgresQueryDrawerHeader
                header="Query"
                marginStyles={{ marginTop: '32px', marginBottom: '8px' }}
              />
              <Box mt="24px">
                <InfoAlertWithDescription description="This query returned no data for the selected preview date." />
              </Box>
            </>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
